import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { isConfirmedOrphanSession } from '../lib/orphanSession';
import type { User as SupabaseUser, Session as SupabaseSession } from '@supabase/supabase-js';

interface User {
  id: string;
  email: string;
  first_name: string;
  company_name: string;
  username_slug?: string | null;
  user_profile?: string;
  website_url?: string;
  image_preferences?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  blog_schedule_enabled: boolean;
  blog_schedule_frequency?: string;
  blog_schedule_days_of_week?: number[];
  blog_schedule_time?: string;
  blog_schedule_timezone?: string;
  last_blog_sent_at?: string;
  next_blog_scheduled_at?: string;
  auth_user_id?: string;
  is_admin: boolean;
  is_super_admin?: boolean;
  is_pro: boolean;
  widget_api_key?: string;
  widget_enabled?: boolean;
  widget_last_used_at?: string;
  email_greeting_style?: string;
  email_closing_style?: string;
  zapier_api_key?: string;
  whatsapp_phone?: string | null;
  whatsapp_verified_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface ActiveCancellation {
  id: string;
  effective_end_date: string;
  expired: boolean;
}

interface AuthContextType {
  authUser: SupabaseUser | null;
  user: User | null;
  session: SupabaseSession | null;
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isSupportUser: boolean;
  needsOnboarding: boolean;
  activeCancellation: ActiveCancellation | null;
  subscriptionEnded: boolean;
  authNotice: string | null;
  signInWithMagicLink: (email: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authUser, setAuthUser] = useState<SupabaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<SupabaseSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isSupportUser, setIsSupportUser] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [activeCancellation, setActiveCancellation] = useState<ActiveCancellation | null>(null);
  const [authNotice, setAuthNotice] = useState<string | null>(null);

  // Cache voor user data om spinner te vermijden bij app-switching
  const [cachedUserData, setCachedUserData] = useState<{
    user: User;
    isAdmin: boolean;
    isSuperAdmin: boolean;
    isSupportUser: boolean;
    needsOnboarding: boolean;
  } | null>(null);

  // Ref om altijd de meest recente cached data te kunnen benaderen in callbacks
  const cachedUserDataRef = useRef(cachedUserData);
  cachedUserDataRef.current = cachedUserData;

  // Bewaakt welke user-id momenteel ingelogd is. Supabase JS v2 emit `SIGNED_IN`
  // óók bij tab-zichtbaarheid en cross-tab storage-events met dezelfde user.
  // Door de identiteit te vergelijken kunnen we die "her-aanmeldingen" silent
  // afhandelen i.p.v. de spinner te flashen bij elke tabwissel.
  const currentAuthUserIdRef = useRef<string | null>(null);

  // Voorkomt herhaalde signOut-calls wanneer loadUserData meerdere keren
  // loopt voor dezelfde wees-sessie (mount + auth-events, StrictMode):
  // onthoudt voor welke auth uid we al hebben uitgelogd.
  const orphanSignedOutUidRef = useRef<string | null>(null);

  useEffect(() => {
    // Check active session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthUser(session?.user ?? null);
      currentAuthUserIdRef.current = session?.user?.id ?? null;
      if (session?.user) {
        loadUserData(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      (async () => {
        const previousAuthUserId = currentAuthUserIdRef.current;
        const newAuthUserId = session?.user?.id ?? null;
        const userIdentityChanged = previousAuthUserId !== newAuthUserId;
        currentAuthUserIdRef.current = newAuthUserId;

        // Onderscheid maken tussen echte auth events en token refresh/visibility.
        // SIGNED_IN telt alleen als "echt" wanneer de user-identiteit verandert —
        // anders is het een herhaalde signal na tab-focus / storage-event /
        // token-refresh.
        const isRealAuthEvent =
          event === 'SIGNED_OUT' ||
          event === 'PASSWORD_RECOVERY' ||
          (event === 'SIGNED_IN' && userIdentityChanged);

        // Session wordt altijd bijgewerkt (nieuwe access_token na refresh).
        // authUser alleen wanneer de identiteit verandert — Supabase reikt bij
        // tab-focus een nieuwe object-referentie aan met dezelfde data, en
        // schermen die `authUser` in hun useEffect-deps hebben staan zouden
        // anders telkens hun data opnieuw fetchen (= eigen spinner).
        setSession(session);
        if (userIdentityChanged) {
          setAuthUser(session?.user ?? null);
        }

        if (session?.user) {
          if (isRealAuthEvent) {
            setLoading(true);
            await loadUserData(session.user.id, false);
          }
          // Anders: SIGNED_IN met dezelfde user, INITIAL_SESSION,
          // TOKEN_REFRESHED, USER_UPDATED — `user` is al actueel. Een silent
          // refetch zou alleen object-referenties wisselen en downstream
          // re-fetches in schermen veroorzaken (Dashboard, AdminDashboard,
          // EmailCampaigns, ...). Wie verse data nodig heeft roept
          // refreshUser() expliciet aan.
        } else {
          setUser(null);
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setIsSupportUser(false);
          setNeedsOnboarding(false);
          setActiveCancellation(null);
          setCachedUserData(null);
          setLoading(false);
        }
      })();
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserData = async (authUserId: string, silent: boolean = false) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUserId)
        .maybeSingle();

      if (error) {
        console.error('Error loading user data:', error);
      } else if (data) {
        setUser(data);
        setIsAdmin(data.is_admin || false);
        setIsSuperAdmin(data.is_super_admin || false);

        const { data: supportUser } = await supabase
          .from('support_users')
          .select('is_active')
          .eq('auth_user_id', authUserId)
          .maybeSingle();

        setIsSupportUser(supportUser?.is_active || false);

        const { data: onboardingProgress } = await supabase
          .from('user_onboarding_progress')
          .select('completed, skipped')
          .eq('user_id', data.id)
          .maybeSingle();

        const onboardingComplete = onboardingProgress?.completed || onboardingProgress?.skipped || false;
        const needsOnboardingValue = !onboardingComplete && !data.is_admin && !(supportUser?.is_active || false);
        setNeedsOnboarding(needsOnboardingValue);

        const { data: cancellation } = await supabase
          .from('subscription_cancellations')
          .select('id, effective_end_date')
          .eq('user_id', data.id)
          .is('reversed_at', null)
          .maybeSingle();
        if (cancellation) {
          const todayStr = new Date().toISOString().slice(0, 10);
          setActiveCancellation({
            id: cancellation.id,
            effective_end_date: cancellation.effective_end_date,
            expired: cancellation.effective_end_date < todayStr,
          });
        } else {
          setActiveCancellation(null);
        }

        // Bewaar in cache voor volgende keer
        setCachedUserData({
          user: data,
          isAdmin: data.is_admin || false,
          isSuperAdmin: data.is_super_admin || false,
          isSupportUser: supportUser?.is_active || false,
          needsOnboarding: needsOnboardingValue,
        });

        // Note: redirect naar /onboarding gebeurt nu declaratief via UserGuard
        // en AuthVerify. Geen harde window.location.href meer — die veroorzaakte
        // een volledige page reload en een flash van het dashboard.
      } else {
        // Query geslaagd maar geen users-rij bij deze auth uid. Zonder guard
        // ontstaat een navigatieloop: Dashboard stuurt naar /login (geen user),
        // Login stuurt terug naar /dashboard (wel authUser), tot Firefox de
        // History API afkapt ("The operation is insecure", user_errors
        // bd93369c, 2026-07-11). Pure support- en admin-accounts hebben ook
        // geen users-rij, dus alleen uitloggen als die er evenmin zijn; RLS
        // laat een sessie zijn eigen rij in beide tabellen lezen.
        const [adminResult, supportResult] = await Promise.all([
          supabase
            .from('admin_users')
            .select('id')
            .eq('auth_user_id', authUserId)
            .maybeSingle(),
          supabase
            .from('support_users')
            .select('id')
            .eq('auth_user_id', authUserId)
            .maybeSingle(),
        ]);

        if (isConfirmedOrphanSession(adminResult, supportResult) && orphanSignedOutUidRef.current !== authUserId) {
          orphanSignedOutUidRef.current = authUserId;
          console.error('Sessie zonder account-rij (auth uid ' + authUserId + '); uitloggen om navigatieloop te voorkomen');
          setAuthNotice('Deze inlogsessie hoort niet bij een actief account en is afgesloten. Log opnieuw in of neem contact op via hallo@dibbi.nl.');
          await supabase.auth.signOut();
          // De SIGNED_OUT event in onAuthStateChange ruimt de rest van de state op.
        }
      }
    } catch (err) {
      console.error('Error loading user:', err);
    } finally {
      // Bij silent mode geen loading state wijzigen
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const signInWithMagicLink = async (email: string) => {
    try {
      setLoading(true);
      setAuthNotice(null);

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-magic-link`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send magic link');
      }
    } catch (error) {
      console.error('Error sending magic link:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithPassword = async (email: string, password: string) => {
    try {
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        await loadUserData(data.user.id);

        const userData = await supabase
          .from('users')
          .select('is_admin')
          .eq('auth_user_id', data.user.id)
          .maybeSingle();

        const supportUserData = await supabase
          .from('support_users')
          .select('is_active')
          .eq('auth_user_id', data.user.id)
          .maybeSingle();

        const isAdminUser = userData.data?.is_admin || false;
        const isSupportUserActive = supportUserData.data?.is_active || false;

        if (!isAdminUser && !isSupportUserActive) {
          await supabase.auth.signOut();
          throw new Error('Dit account heeft geen admin rechten');
        }
      }
    } catch (error) {
      console.error('Error signing in with password:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };


  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setAuthUser(null);
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsSupportUser(false);
      setNeedsOnboarding(false);
      setActiveCancellation(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (authUser) {
      await loadUserData(authUser.id, true);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        authUser,
        user,
        session,
        loading,
        isAdmin,
        isSuperAdmin,
        isSupportUser,
        needsOnboarding,
        activeCancellation,
        subscriptionEnded: activeCancellation?.expired ?? false,
        authNotice,
        signInWithMagicLink,
        signInWithPassword,
        signOut,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
